import {
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  User,
  XCircle,
} from 'lucide-react';
import { isMinor as isMinorFromBirthDate } from '@digitaltattoo/shared-utils';

export function parseContractSignatures(raw: unknown): Record<string, { dataUrl?: string }> {
  if (!raw) return {};
  let x: any = raw;
  if (typeof x === 'string') {
    try {
      x = JSON.parse(x);
    } catch {
      return {};
    }
  }
  return x && typeof x === 'object' ? x : {};
}

export function parseIdCardPhotosList(
  raw: unknown,
): Array<{ role: string; side: string; dataUrl: string }> {
  if (!raw) return [];
  let x: any = raw;
  if (typeof x === 'string') {
    try {
      x = JSON.parse(x);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(x)) return [];
  return x
    .filter((p: any) => p && typeof p.dataUrl === 'string')
    .map((p: any) => ({
      role: String(p.role ?? '').toUpperCase(),
      side: String(p.side ?? 'front').toLowerCase() === 'back' ? 'back' : 'front',
      dataUrl: p.dataUrl as string,
    }));
}

export function formatConsultationDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function formatConsultationTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const STATUS_BADGE_CONFIG: Record<
  string,
  { bg: string; text: string; label: string; icon: typeof Calendar }
> = {
  SCHEDULED: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Programmé', icon: Calendar },
  CONFIRMED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Confirmé', icon: CheckCircle },
  PENDING_PAYMENT: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    label: 'Paiement en attente',
    icon: DollarSign,
  },
  CHECKED_IN: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Arrivé', icon: User },
  IN_PROGRESS: { bg: 'bg-amber-100', text: 'text-amber-900', label: 'Pris en charge', icon: Clock },
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé', icon: CheckCircle },
  AWAITING_CHECKOUT: {
    bg: 'bg-violet-100',
    text: 'text-violet-800',
    label: 'Attente caisse',
    icon: DollarSign,
  },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Annulé', icon: XCircle },
  NO_SHOW: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Absent', icon: XCircle },
  RESCHEDULED: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Reporté', icon: Calendar },
};

/** Canonical appointment status values for filters / legends (order = display order). */
export const APPOINTMENT_STATUS_OPTIONS = [
  'SCHEDULED',
  'CONFIRMED',
  'PENDING_PAYMENT',
  'CHECKED_IN',
  'IN_PROGRESS',
  'AWAITING_CHECKOUT',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
  'RESCHEDULED',
] as const;

export const TYPE_BADGE_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  CONSULTATION: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Consultation' },
  TATTOO_SESSION: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Tatouage' },
  PIERCING: { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Piercing' },
  TOUCH_UP: { bg: 'bg-green-100', text: 'text-green-800', label: 'Retouche' },
  REMOVAL: { bg: 'bg-red-100', text: 'text-red-800', label: 'Détatouage' },
  AFTERCARE: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Suivi' },
};

/** French label for an appointment status (IN_PROGRESS → « Pris en charge »). */
export function appointmentStatusLabel(status: string): string {
  return STATUS_BADGE_CONFIG[status]?.label ?? status;
}

/** Normalize loose API appointment payloads into consultation-shaped objects. */
export function normalizeAppointmentConsultationData(raw: any) {
  const pricing =
    raw?.pricing && typeof raw.pricing === 'object'
      ? raw.pricing
      : {};

  const preparation =
    raw?.preparation && typeof raw.preparation === 'object'
      ? raw.preparation
      : {};

  const reminders =
    raw?.reminders && typeof raw.reminders === 'object'
      ? raw.reminders
      : {};

  const clientBirthDate = raw?.clientDateOfBirth ?? raw?.client?.dateOfBirth ?? null;
  const rawMinorFlag = raw?.clientIsMinor ?? raw?.client?.isMinor ?? null;
  const clientIsMinor: boolean | null = clientBirthDate
    ? isMinorFromBirthDate(clientBirthDate)
    : rawMinorFlag === true
      || rawMinorFlag === 1
      || rawMinorFlag === '1'
      || rawMinorFlag === 'true'
      || rawMinorFlag === 't'
      ? true
      : rawMinorFlag === false
        || rawMinorFlag === 0
        || rawMinorFlag === '0'
        || rawMinorFlag === 'false'
        || rawMinorFlag === 'f'
        ? false
        : null;

  const legalGuardian =
    raw?.legalGuardian &&
    typeof raw.legalGuardian === 'object'
      ? {
          firstName: String(
            raw.legalGuardian.firstName ?? '',
          ),

          lastName: String(
            raw.legalGuardian.lastName ?? '',
          ),

          email: raw.legalGuardian.email
            ? String(raw.legalGuardian.email)
            : undefined,

          phone: raw.legalGuardian.phone
            ? String(raw.legalGuardian.phone)
            : undefined,

          relationshipToMinor:
            raw.legalGuardian.relationshipToMinor
              ? String(
                  raw.legalGuardian.relationshipToMinor,
                )
              : undefined,

          relationshipDetails:
            raw.legalGuardian.relationshipDetails != null
              ? String(
                  raw.legalGuardian.relationshipDetails,
                )
              : null,
        }
      : null;

  return {
    id: String(raw?.id ?? ''),

    clientFirstName: String(
      raw?.clientFirstName ?? '',
    ),

    clientLastName: String(
      raw?.clientLastName ?? '',
    ),

    clientEmail: String(
      raw?.clientEmail ?? '',
    ),

    // Client mineur
    clientIsMinor,

    // Tuteur légal
    legalGuardian,

    practitionerId:
      (raw?.practitionerId ?? null) as string | null,

    practitionerFirstName:
      (raw?.practitionerFirstName ??
        null) as string | null,

    practitionerLastName:
      (raw?.practitionerLastName ??
        null) as string | null,

    reservedPractitionerId:
      (raw?.reservedPractitionerId ??
        null) as string | null,

    reservedPractitionerFirstName:
      (raw?.reservedPractitionerFirstName ??
        null) as string | null,

    reservedPractitionerLastName:
      (raw?.reservedPractitionerLastName ??
        null) as string | null,

    takenInChargeAt:
      (raw?.takenInChargeAt ??
        null) as string | null,

    shopName: String(
      raw?.shopName ?? '',
    ),

    serviceName: String(
      raw?.serviceName ?? '',
    ),

    scheduledAt: String(
      raw?.scheduledAt ?? '',
    ),

    estimatedEndTime: String(
      raw?.estimatedEndTime ?? '',
    ),

    actualStartTime:
      raw?.actualStartTime as string | undefined,

    actualEndTime:
      raw?.actualEndTime as string | undefined,

    status: String(
      raw?.status ?? '',
    ),

    type: String(
      raw?.type ?? '',
    ),

    notes: String(
      raw?.notes ?? '',
    ),

    clientNotes:
      raw?.clientNotes as string | undefined,

    checkInCode:
      (raw?.checkInCode ??
        null) as string | null,

    project:
      raw?.project &&
      typeof raw.project === 'object'
        ? {
            style: String(
              raw.project.style ?? '',
            ),

            placement: String(
              raw.project.placement ?? '',
            ),

            size: String(
              raw.project.size ?? '',
            ),

            description: String(
              raw.project.description ?? '',
            ),

            colorType: String(
              raw.project.colorType ?? '',
            ),

            notesToArtist:
              (raw.project.notesToArtist ??
                null) as string | null,
          }
        : null,

    referenceImages: Array.isArray(
      raw?.referenceImages,
    )
      ? raw.referenceImages
          .filter(
            (img: any) =>
              img &&
              typeof img.url === 'string',
          )
          .map((img: any) => ({
            id: String(
              img.id ?? img.url,
            ),

            url: String(img.url),

            thumbnailUrl:
              (img.thumbnailUrl ??
                null) as string | null,

            fileName: String(
              img.fileName ?? 'image',
            ),

            mimeType: String(
              img.mimeType ?? 'image/*',
            ),
          }))
      : [],

    pricing: {
      basePrice: Number(
        pricing.basePrice ?? 0,
      ),

      piercingTypes:
        pricing.piercingTypes,

      piercingType:
        pricing.piercingType,

      workflowSupplements:
        pricing.workflowSupplements,

      supplements:
        pricing.supplements,

      subtotal: Number(
        pricing.subtotal ?? 0,
      ),

      deposit: Number(
        pricing.deposit ?? 0,
      ),

      depositAmount:
        pricing.depositAmount == null
          ? null
          : Number(pricing.depositAmount),

      total: Number(
        pricing.total ?? 0,
      ),

      paymentMethod: String(
        pricing.paymentMethod ?? '',
      ),

      currency: String(
        pricing.currency ?? 'EUR',
      ),
    },

    preparation: {
      beforeAppointment: Array.isArray(
        preparation.beforeAppointment,
      )
        ? preparation.beforeAppointment
        : [],

      whatToBring: Array.isArray(
        preparation.whatToBring,
      )
        ? preparation.whatToBring
        : [],

      restrictions: Array.isArray(
        preparation.restrictions,
      )
        ? preparation.restrictions
        : [],
    },

    reminders: {
      enabled: Boolean(
        reminders.enabled,
      ),

      methods: Array.isArray(
        reminders.methods,
      )
        ? reminders.methods
        : [],

      daysBefore: Array.isArray(
        reminders.daysBefore,
      )
        ? reminders.daysBefore
        : [],

      customMessage:
        reminders.customMessage as
          | string
          | undefined,
    },

    depositRequired:
      raw?.depositRequired as
        | boolean
        | undefined,

    depositAmount:
      (raw?.depositAmount ??
        null) as number | null,

    depositStatus:
      raw?.depositStatus as
        | 'NOT_REQUIRED'
        | 'PENDING'
        | 'PAID'
        | 'FAILED'
        | 'REFUNDED'
        | undefined,

    createdAt: String(
      raw?.createdAt ?? '',
    ),

    updatedAt: String(
      raw?.updatedAt ?? '',
    ),
  };
}
